import React, { useState } from 'react';
import { doc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import toast from 'react-hot-toast';

const DataMigration = () => {
  const [loading, setLoading] = useState(null);

  const cleanData = (data) => {
    if (Array.isArray(data)) {
      return data.map(v => cleanData(v));
    }
    if (data !== null && typeof data === 'object') {
      if (data.$oid) return data.$oid;
      if (data.$date) return new Date(data.$date);
      if (data.$numberLong) return Number(data.$numberLong);

      const newObj = {};
      for (const key in data) {
        newObj[key] = cleanData(data[key]);
      }
      return newObj;
    }
    return data;
  };

  const migrateCollection = async (fileName, collectionName, idField) => {
    setLoading(fileName);
    toast.loading(`Migrating ${collectionName}...`);
    const idKey = idField || '_id';

    try {
      const response = await fetch(`/mongodb/${fileName}.json`);
      const data = await response.json();
      const batch = writeBatch(db);

      data.forEach(item => {
        const id = item[idKey]?.$oid || item[idKey];
        if (!id) {
          console.warn(`Skipping item without ID in ${fileName}:`, item);
          return;
        }

        const dataToSet = (idKey === '_id') ? (({ [idKey]: _, ...rest }) => rest)(item) : item;
        const cleanedData = cleanData(dataToSet);

        const docRef = doc(db, collectionName, id.toString());
        batch.set(docRef, cleanedData);
      });

      await batch.commit();
      toast.dismiss();
      toast.success(`${collectionName} migrated successfully!`);
    } catch (error) {
      console.error(`Error migrating ${collectionName}: `, error);
      toast.dismiss();
      toast.error(`Error migrating ${collectionName}.`);
    } finally {
      setLoading(null);
    }
  };

  const migrationTasks = [
    { name: 'Districts', file: 'districts', collection: 'districts', idField: 'name' },
    { name: 'Literary Fields', file: 'literary-fields', collection: 'literary-fields', idField: 'name' },
    { name: 'Users', file: 'users', collection: 'users' },
    { name: 'Branches', file: 'branches', collection: 'branches' },
    { name: 'Books', file: 'books', collection: 'books' },
    { name: 'Board Members', file: 'boardmembers', collection: 'boardmembers' },
    { name: 'Events', file: 'events', collection: 'events' }
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Data Migration</h1>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <p className="mb-2 text-red-600 font-semibold">Important: Your database contains data in an incorrect format, causing errors across the site.</p>
        <p className="mb-6 text-gray-700">Please run the migration for <strong className="font-bold">ALL</strong> collections below, one by one. This will clean the data and permanently fix the errors.</p>
        <div className="space-y-4">
          {migrationTasks.map(task => (
            <div key={task.name} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
              <p className="font-semibold text-gray-800">{task.name}</p>
              <button 
                onClick={() => migrateCollection(task.file, task.collection, task.idField)}
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400 w-48 text-center"
              >
                {loading === task.file ? 'Migrating...' : `Migrate ${task.name}`}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DataMigration;
